/**
 * A round avatar, falling back to initials.
 *
 * Most accounts have no photo for a long time, so the fallback is the common
 * case rather than the exception. The colour is derived from the name so the
 * same person is the same colour everywhere, which is what makes a list
 * scannable before you have read any of it.
 */

import './Avatar.css'

/**
 * Muted, mid-dark hues that take white text in either theme.
 *
 * Kept away from the brand's own green (150°) so an avatar is never mistaken
 * for a piece of interface chrome.
 */
const HUES = [205, 262, 330, 22, 44, 190, 300, 12]

/**
 * Coerce a possibly-null name to a string.
 *
 * A default parameter only fires for undefined, and a direct chat's name is
 * explicitly NULL — groups have names, one-to-one chats do not. Relying on
 * `name = ''` meant hueFor(null) reached null.length and threw, which took
 * React down with it and left the whole chat list blank. Found by the first
 * browser test ever run against this app.
 */
const asName = value => (value == null ? '' : String(value))

function hueFor (rawName) {
  const name = asName(rawName)
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0
  return HUES[Math.abs(hash) % HUES.length]
}

function initials (rawName) {
  const parts = asName(rawName).trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return ''
  return (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase()
}

export default function Avatar ({ name, url, size = 48, group = false }) {
  const text = initials(name)

  return (
    <span
      className="av"
      style={{
        width: size,
        height: size,
        // Fixed saturation and lightness so every avatar carries the same
        // weight — one loud colour in a list pulls the eye off the names.
        background: url ? undefined : `hsl(${hueFor(name)} 34% 46%)`,
        fontSize: Math.round(size * 0.36)
      }}
    >
      {url
        ? <img className="av__img" src={url} alt="" />
        : text
          ? <span className="av__text">{text}</span>
          : (
            <svg className="av__glyph" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              {group ? (
                <>
                  <circle cx="9" cy="9.5" r="3.1" stroke="currentColor" strokeWidth="1.7" />
                  <path d="M3.6 19v-1.3A3.6 3.6 0 0 1 7.2 14h3.6a3.6 3.6 0 0 1 3.6 3.7V19"
                    stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                  <path d="M16.2 7.2a3 3 0 0 1 0 5.6M17.4 14.6c2 .4 3 1.9 3 3.7V19"
                    stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                </>
              ) : (
                <>
                  <circle cx="12" cy="8.6" r="3.6" stroke="currentColor" strokeWidth="1.7" />
                  <path d="M4.8 19.4v-.8a5 5 0 0 1 5-5h4.4a5 5 0 0 1 5 5v.8"
                    stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                </>
              )}
            </svg>
          )}
    </span>
  )
}
